import { RouterButton } from 'components/Button/RouterButton';
import { Icon } from 'components/Icon/Icon';
import { BodyMicro } from 'components/Text/BodyMicro';
import { H6 } from 'components/Text/H6';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { ListingSearchType } from 'modules/cms/api/types/CmsApiPage';
import { searchGetJobsRoutePath } from 'modules/search/routing/helpers/searchGetJobsRoutePath';
import { searchGetVolopsRoutePath } from 'modules/search/routing/helpers/searchGetVolopsRoutePath';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { showModal } from 'zustand-stores/modalStore';

import {
  CmsSocialSidebarContainer,
  CmsSocialSidebarFAQLinkContainerBase,
  CmsSocialSidebarNewsletterButtonContainer,
  CmsSocialSidebarRecentJobsContainer,
  CmsSocialSidebarShareButtonContainer,
  CmsSocialSidebarTooltipContainer,
} from './CmsSocialSidebar.styles';

type Props = {
  listingSearchType: ListingSearchType;
  hasSocialShare: boolean;
  subscribe: () => void;
  canSubscribe: boolean;
  isSubscribed: boolean;
  shareUrl: Record<UserLocale, string>;
  subsiteSubscribeText: string;
};

export function CmsSocialSidebar({
  canSubscribe,
  isSubscribed,
  subscribe,
  listingSearchType,
  hasSocialShare,
  shareUrl,
  subsiteSubscribeText,
}: Props) {
  const handleShare = () => {
    showModal('SHARE_LISTING', {
      listingType: 'URL',
      messageOverride: getText('Check out this article'),
      shareUrl,
    });
  };

  if (!canSubscribe && !hasSocialShare) {
    return null;
  }

  const searchListingMap: Record<
    ListingSearchType,
    { headline: string; description: string; buttonText: string; url: string }
  > = {
    JOB: {
      headline: getText('Land Your Dream Job'),
      description: getText(
        'Careers that help you move from intention to action',
      ),
      buttonText: getText('Search Jobs'),
      url: searchGetJobsRoutePath({
        lang: CURRENT_LOCALE,
        filtersByName: { locale: CURRENT_LOCALE },
      }),
    },
    VOLOP: {
      headline: getText('Make a Difference'),
      description: getText(
        'Search for volunteer opportunities in your community',
      ),
      buttonText: getText('Search'),
      url: searchGetVolopsRoutePath({
        lang: CURRENT_LOCALE,
        filtersByName: { actionType: ['VOLOP'], locale: CURRENT_LOCALE },
      }),
    },
    RECIPES_FOR_ACTION: {
      headline: getText('Submit a Recipe'),
      description: getText(
        'Share how you’ve made a difference in your community to inspire others.',
      ),
      buttonText: getText('Submit a Recipe'),
      // we might want to localise this URL in the future:
      // context: https://www.pivotaltracker.com/story/show/187107721
      url: '/en/recipes-for-action/submission-form',
    },
  };

  return (
    <CmsSocialSidebarContainer>
      {listingSearchType in searchListingMap ? (
        <CmsSocialSidebarRecentJobsContainer>
          <H6 lineHeight={1} mb="16px">
            {searchListingMap[listingSearchType].headline}
          </H6>
          <BodyMicro lineHeight={1.2} mb="16px">
            {searchListingMap[listingSearchType].description}
          </BodyMicro>
          <RouterButton
            data-qa-id="sidebar-recent-listings-button"
            onClick={() =>
              trackClicked(searchListingMap[listingSearchType].buttonText, {
                context: 'CMS Page',
              })
            }
            to={searchListingMap[listingSearchType].url}
            state={{ filterType: listingSearchType }}
            variant="primary"
            size="small"
          >
            {searchListingMap[listingSearchType].buttonText}
          </RouterButton>
        </CmsSocialSidebarRecentJobsContainer>
      ) : null}
      <CmsSocialSidebarTooltipContainer>
        {canSubscribe ? (
          <Tooltip
            variant="filled"
            content={
              isSubscribed
                ? getText('You are now subscribed!')
                : subsiteSubscribeText
            }
          >
            <CmsSocialSidebarNewsletterButtonContainer
              type="button"
              onClick={subscribe}
              data-qa-id="sidebar-subscribe-to-subsite"
              disabled={isSubscribed}
              aria-label={getText('Subscribe')}
            >
              <Icon name="newsletter" size={30} />
            </CmsSocialSidebarNewsletterButtonContainer>
          </Tooltip>
        ) : null}
        {hasSocialShare ? (
          <Tooltip variant="filled" content={getText('Share')}>
            <CmsSocialSidebarShareButtonContainer
              type="button"
              onClick={handleShare}
              data-qa-id="sidebar-social-share-button"
              aria-label={getText('Share')}
            >
              <Icon name="share" size={24} />
            </CmsSocialSidebarShareButtonContainer>
          </Tooltip>
        ) : null}
        {listingSearchType === 'RECIPES_FOR_ACTION' ? (
          <Tooltip
            variant="filled"
            content={getText('Frequently Asked Questions (FAQ)')}
          >
            {/* !!!: wrapped with div as Tooltip doesn't seem to detect
                     the Link component as a valid child
             */}
            <div>
              <CmsSocialSidebarFAQLinkContainerBase
                to="en/recipes-for-action/faq"
                data-qa-id="sidebar-faq-button"
                aria-label={getText('Frequently Asked Questions (FAQ)')}
              >
                <Icon name="question-circle" size={24} />
              </CmsSocialSidebarFAQLinkContainerBase>
            </div>
          </Tooltip>
        ) : null}
      </CmsSocialSidebarTooltipContainer>
    </CmsSocialSidebarContainer>
  );
}
